/**
 * Service 抽象层基础接口
 * 所有 Service 实现（Mock / HTTP）均需遵循此契约
 */

/**
 * 统一前后端 API 响应契约。
 *
 * 与后端 Java ApiResponse<T> 完全对齐：code=200 表示成功。
 * 曾与旧形态 {success, error} 双轨并存（store 大量读 res.success / res.error），
 * 现收敛为单形态；成功判定用 code === 200 或 isApiOk()。
 */
export interface ApiResponse<T> {
  code: number
  data?: T
  message?: string
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
}

/**
 * 把拦截器已解包的业务数据包装为 ApiResponse<T>。
 * 与后端 Java ApiResponse<T> 完全对齐：code=200 表示成功。
 *
 * <p>message 无默认值（曾用 'success'）：拦截器开始按 code 判定后，ApiResponse.success(null)
 * 这类无 data 的成功响应会让 res.data 为 undefined，任何 `showError(res.message)` /
 * `error.value = res.message ?? 兜底` 的写法都会把字符串 "success" 当成失败原因弹给用户。
 */
export function wrapResponse<T>(data: T, message?: string): ApiResponse<T> {
  return { code: 200, data, message }
}

/** 统一判断 ApiResponse 是否成功（code === 200） */
export function isApiOk(r: { code?: number } | null | undefined): boolean {
  return r?.code === 200
}

export interface ApiError {
  code: string
  message: string
  details?: Record<string, string[]>
}

/**
 * 日志类 Excel 导出结果：blob 附件 + 截断信息（经 X-Total-Count/X-Export-Rows/
 * X-Export-Truncated/X-Export-Limit 响应头透出；跨域读取依赖后端 CorsConfig.exposedHeaders）。
 */
export interface LogExportResult {
  blob: Blob
  /** 筛选条件命中的总条数（count 与分批读行之间允许有并发漂移，落盘行数看 rows） */
  total?: number
  /** 真正写进 xlsx 的数据行数，与文件内容逐字对应 */
  rows?: number
  /** 超过后端导出上限时为 true，blob 仅含按时间倒序的前 limit 条 */
  truncated: boolean
  /** 后端导出上限（经 X-Export-Limit 下发，供前端提示文案使用，避免两侧各自硬编码） */
  limit?: number
}

/** 日志导出属重请求（服务端 count + 分批滚动 + SXSSF 写盘，上限 5 万行），放宽全局 15s 超时 */
export const LOG_EXPORT_TIMEOUT_MS = 60_000

/**
 * 模拟网络延迟的工具函数
 */
export function mockDelay(ms: number = 300): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 生成唯一 ID
 */
export function generateId(): string {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

/**
 * 统一的 localStorage 键名前缀，避免冲突
 */
export const STORAGE_PREFIX = 'ledger_ai_'

export function storageKey(name: string): string {
  return `${STORAGE_PREFIX}${name}`
}

// =================== 修复 F23：mock 文件抽公共工具 ===================

/**
 * 从 localStorage 读取 JSON 数组，统一容错处理。
 * 任何解析失败或 key 不存在都返回空数组，避免调用方再写 try/catch。
 */
export function loadFromStorage<T>(key: string): T[] {
  if (typeof localStorage === 'undefined') return []
  const raw = localStorage.getItem(key)
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

/**
 * 把对象中指定的字符串字段统一转换为 Date 实例。
 * 用于把 localStorage 反序列化的 ISO 字符串重建回 Date 类型。
 * 不可变：不修改入参，返回新对象。
 *
 * 泛型约束为 `object` 而非 `Record<string, unknown>`，以兼容具体接口类型（如 Order/Payment/Subscription），
 * 这些接口没有显式字符串索引签名，但仍可与索引可读对象互转（通过双重断言）。
 */
export function withDateFields<T extends object>(
  obj: T,
  keys: readonly string[],
): T {
  const out = { ...(obj as Record<string, unknown>) }
  for (const k of keys) {
    const v = out[k]
    if (typeof v === 'string') {
      const d = new Date(v)
      if (!isNaN(d.getTime())) out[k] = d
    }
  }
  return out as unknown as T
}
